<template>
  <tr
    class="finding-row data-row"
    :class="{ 'is-active': active, 'is-selected': selected, 'is-accepted': accepted, 'is-rejected': rejected }"
    tabindex="0"
    :data-id="finding.id"
    :aria-label="`Finding ${finding.badgeIndex}, ${location}`"
    @click="$emit('focus', finding)"
    @keydown.enter.self.prevent="$emit('focus', finding)"
    @keydown.space.self.prevent="$emit('focus', finding)">
    <td v-if="canReviewAiAnalysis" class="col-select">
      <input type="checkbox" :checked="selected" :aria-label="`Select finding ${finding.badgeIndex}`" @click.stop="$emit('select', { id: finding.id, checked: $event.target.checked })" />
    </td>
    <td class="col-id"><span class="badge">{{ finding.badgeIndex }}</span></td>
    <td class="col-location">
      <div class="loc-main cell-main" :title="location">{{ location }}</div>
      <div class="loc-sub cell-sub" :title="subline">{{ subline }}</div>
    </td>
    <!-- Component: the row's own entry to the picker (approved showcase ai-analysis-tag-picker-showcase.html). -->
    <td class="col-component">
      <span v-if="!canReviewAiAnalysis">{{ component ? component.name : notLinkedLabel }}</span>
      <span v-else-if="component" class="comp-wrap">
        <button
          type="button"
          class="comp"
          :title="`${component.name} · ${component.type || 'Component'}`"
          :aria-label="`Component of finding ${finding.badgeIndex}: ${component.name}. Change`"
          @click.stop="$emit('link', finding)">
          <v-icon :size="15" class="comp__icon" :class="typeClass">{{ componentIcon }}</v-icon>
          <span class="comp__name">{{ component.name }}</span>
          <v-icon :size="14" class="comp__caret" aria-hidden="true">mdi-menu-down</v-icon>
        </button>
        <button type="button" class="comp-unlink" title="Remove link" :aria-label="`Remove the link of finding ${finding.badgeIndex}`" @click.stop="$emit('unlink', finding)">
          <v-icon :size="13" class="comp-unlink__icon">mdi-close</v-icon>
        </button>
      </span>
      <button v-else type="button" class="comp comp--none" title="Link to a component" :aria-label="`Link finding ${finding.badgeIndex} to a component`" @click.stop="$emit('link', finding)">
        <v-icon :size="15" class="comp__add" aria-hidden="true">mdi-link-variant-plus</v-icon>
        <span class="comp__name">{{ notLinkedLabel }}</span>
      </button>
    </td>
    <td class="num" :title="`Stored as ${finding.coatingBreakdown || emDash}`">{{ ri }}</td>
    <td class="col-cat center" :title="`Stored as ${finding.pittingCategory || emDash}`">{{ cat }}</td>
    <td class="center"><confidence-ring :value="Number(finding.detectionConfidence) || 0" /></td>
    <td class="num" title="Shoelace polygon area ÷ image area">{{ areaPercent }}</td>
    <td class="col-scab"><span class="chip chip--cell" :class="scabClass">{{ finding.scabMeasure || emDash }}</span></td>
    <td class="center"><confidence-ring :value="Number(finding.scabConfidence) || 0" /></td>
    <td class="col-notes" :title="notes">{{ notes }}</td>
    <td class="actions col-actions">
      <span v-if="accepted" class="decision-pill decision-pill--accepted"><v-icon :size="12" class="decision-pill__icon">mdi-check</v-icon> Accepted</span>
      <span v-else-if="rejected" class="decision-pill decision-pill--rejected"><v-icon :size="12" class="decision-pill__icon">mdi-close</v-icon> Rejected</span>
      <span v-else class="row-actions">
        <v-btn v-if="canReviewAiAnalysis" icon small class="row-act row-btn row-btn--accept" title="Accept finding" :aria-label="`Accept finding ${finding.badgeIndex}`" @click.stop="$emit('approve', finding)">
          <v-icon :size="18">mdi-check</v-icon>
        </v-btn>
        <v-btn v-if="canReviewAiAnalysis" icon small class="row-act row-btn row-btn--reject" title="Reject finding" :aria-label="`Reject finding ${finding.badgeIndex}`" @click.stop="$emit('reject', finding)">
          <v-icon :size="18">mdi-close</v-icon>
        </v-btn>
        <!-- Rule 06: the kebab stays visible at low emphasis; it opens the detail panel. -->
        <v-btn icon small class="row-more" title="Details and actions" :aria-label="`More actions for finding ${finding.badgeIndex}`" @click.stop="$emit('details', finding)">
          <v-icon :size="18">mdi-dots-vertical</v-icon>
        </v-btn>
      </span>
    </td>
  </tr>
</template>

<script>
import aiAnalysisPermissionMixin from '@/features/Dashboard/utils/aiAnalysisPermissionMixin';
  import { APPROVAL, SCAB, parseMaskPoints, relativeArea, stripRi, stripCat } from '../../utils/findingsMetrics';
  import { EM_DASH, formatPercent } from '../../utils/reportFormat';
  import { componentOfFinding, componentTypeIcon } from '../../utils/findingComponents';
  import { AREA_DECIMALS, NOT_LINKED_LABEL } from './reportConstants';
  import ConfidenceRing from './ConfidenceRing.vue';

  const SCAB_CLASS = Object.freeze({
    [SCAB.YES]: 'chip--scab-yes',
    [SCAB.POSSIBLE]: 'chip--scab-possible',
    [SCAB.NO]: 'chip--scab-no',
  });

  export default {
    mixins: [aiAnalysisPermissionMixin],
    name: 'FindingRow',
    components: { ConfidenceRing },
    props: {
      finding: { type: Object, required: true },
      /** { width, height } of the finding's image; missing sizes give a 0 area. */
      imageSize: { type: Object, default: () => ({}) },
      scanName: { type: String, default: '' },
      active: { type: Boolean, default: false },
      selected: { type: Boolean, default: false },
    },
    data() {
      return { emDash: EM_DASH, notLinkedLabel: NOT_LINKED_LABEL };
    },
    computed: {
      accepted() {
        return this.finding.approvalStatus === APPROVAL.ACCEPTED;
      },
      rejected() {
        return this.finding.approvalStatus === APPROVAL.REJECTED;
      },
      location() {
        return this.finding.locationDescription || EM_DASH;
      },
      subline() {
        return [this.scanName, this.finding.type].filter(Boolean).join(' · ') || EM_DASH;
      },
      component() {
        return componentOfFinding(this.finding);
      },
      componentIcon() {
        return componentTypeIcon(this.component && this.component.type);
      },
      typeClass() {
        return this.component && this.component.type ? `t-${this.component.type}` : '';
      },
      ri() {
        return stripRi(this.finding.coatingBreakdown) || EM_DASH;
      },
      cat() {
        return stripCat(this.finding.pittingCategory) || EM_DASH;
      },
      areaPercent() {
        const area = relativeArea(parseMaskPoints(this.finding.maskPoints), this.imageSize.width, this.imageSize.height);
        return formatPercent(area, AREA_DECIMALS);
      },
      scabClass() {
        return SCAB_CLASS[String(this.finding.scabMeasure || '').toUpperCase()] || 'chip--scab-no';
      },
      notes() {
        return this.finding.scabReason || this.finding.description || EM_DASH;
      },
    },
  };
</script>
